import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {Label} from '@components/common/Label';
import {addLineItem as _addLineItem} from '@account/api';
import {formatErrorMsg, getError} from '@account/utils/errors';
import {translateQuotaUnits} from '@account/utils/quota';
import {useMutation} from '@tanstack/react-query';

const ModifyQuotaForm = ({
  contractId,
  productAccess,
  onAddProduct,
  onClose,
  onError,
  onLoadingChange,
}) => {
  const [targetRaw, setTargetRaw] = useState('');

  const currentTotal = productAccess.quota_total ?? 0;
  const units = productAccess.quota_units ?? '';
  const displayUnits = translateQuotaUnits(units);

  const target = targetRaw === '' ? null : Number(targetRaw);
  const isValidTarget = target !== null && Number.isFinite(target);
  const delta = isValidTarget ? target - currentTotal : null;
  const isDeltaZero = delta === 0;
  const isNegativeDelta = delta !== null && delta < 0;
  const missingVariantId = !productAccess.edp_id;
  const isFloat = delta !== null && delta % 1 !== 0;
  const canSubmit =
    isValidTarget &&
    !isDeltaZero &&
    !isNegativeDelta &&
    !missingVariantId &&
    !isFloat;

  const deltaLabel = useMemo(() => {
    if (delta === null) return null;
    if (isDeltaZero) return 'No change';
    return `${delta > 0 ? '+' : ''}${delta.toLocaleString()} ${displayUnits}`;
  }, [delta, isDeltaZero, displayUnits]);

  const {mutate, isLoading, isError, error} = useMutation(
    () =>
      _addLineItem({
        contractId,
        productVariantId: productAccess.edp_id,
        hasUnlimitedQuota: false,
        quotaLimit: delta,
        quotaUnits: units,
        startDate: productAccess.term_start_date,
        endDate: productAccess.term_end_date,
      }),
    {
      onSuccess: () => {
        onLoadingChange?.(false);
        onAddProduct();
      },
      onError: err => {
        onLoadingChange?.(false);
        onError?.(err);
      },
    }
  );

  const handleSubmit = useCallback(() => {
    onLoadingChange?.(true);
    mutate();
  }, [mutate, onLoadingChange]);

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Sets a new total quota for this product. To adjust quota within a
        specific access period, use the Adjust button in the access periods
        table.
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography variant="subtitle2" color="text.secondary">
          Current quota
        </Typography>
        <Typography variant="h6">
          {currentTotal.toLocaleString()} {displayUnits}
        </Typography>
      </Stack>

      <Stack spacing={1}>
        <Label isInputLabel htmlFor="modify-quota-target">
          New target ({displayUnits})
        </Label>
        <TextField
          autoComplete="off"
          fullWidth
          id="modify-quota-target"
          inputProps={{min: 0, step: 'any'}}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{displayUnits}</InputAdornment>
            ),
          }}
          onChange={e => setTargetRaw(e.target.value)}
          placeholder={currentTotal.toLocaleString()}
          size="small"
          type="number"
          value={targetRaw}
        />
      </Stack>

      <Box
        sx={theme => ({
          borderRadius: 1,
          border: `1px solid ${theme.palette.divider}`,
          p: 2,
          bgcolor: 'grey.50',
        })}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          Quota preview
        </Typography>

        <Stack spacing={0.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Current
            </Typography>
            <Typography variant="body2">
              {currentTotal.toLocaleString()} {displayUnits}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              New target
            </Typography>
            <Typography variant="body2">
              {target !== null
                ? `${target.toLocaleString()} ${displayUnits}`
                : '—'}
            </Typography>
          </Stack>

          <Divider sx={{my: 0.5}} />

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>
              Change
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              color={
                delta === null || isDeltaZero
                  ? 'text.primary'
                  : delta > 0
                    ? 'success.main'
                    : 'error.main'
              }
            >
              {deltaLabel ?? '—'}
            </Typography>
          </Stack>
        </Stack>

        {missingVariantId && (
          <Alert severity="error" sx={{mt: 1}} variant="outlined">
            This product has no variant ID and cannot be submitted to the
            contract manager.
          </Alert>
        )}
        {target !== null && !isValidTarget && (
          <Alert severity="error" sx={{mt: 1}} variant="outlined">
            Please enter a valid number.
          </Alert>
        )}
        {isDeltaZero && (
          <Alert severity="info" sx={{mt: 1}} variant="outlined">
            Target matches current quota — no change will be made.
          </Alert>
        )}
        {isNegativeDelta && (
          <Alert severity="error" sx={{mt: 1}} variant="outlined">
            New target must be greater than the current quota.
          </Alert>
        )}
        {isFloat && (
          <Alert severity="error" sx={{mt: 1}} variant="outlined">
            Quota must be a whole number.
          </Alert>
        )}
      </Box>

      {isError && (
        <Typography color="error" variant="body2">
          {formatErrorMsg(getError(error))}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose} variant="outlined" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!canSubmit || isLoading}
          onClick={handleSubmit}
          variant="contained"
        >
          {isLoading ? 'Saving…' : 'Apply change'}
        </Button>
      </Stack>
    </Stack>
  );
};

ModifyQuotaForm.propTypes = {
  contractId: PropTypes.number.isRequired,
  productAccess: PropTypes.shape({
    edp_id: PropTypes.string,
    name: PropTypes.string,
    quota_total: PropTypes.number,
    quota_units: PropTypes.string,
    term_start_date: PropTypes.string,
    term_end_date: PropTypes.string,
  }).isRequired,
  onAddProduct: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onError: PropTypes.func,
  onLoadingChange: PropTypes.func,
};

export default ModifyQuotaForm;
